/**
 * 미저장 변경사항 검증 + 저장 payload 생성 — 부분 저장(partial save).
 *
 * store 키는 이미 ChangeKey(identity: 또는 cell:)로 정규화돼 들어온다. 같은 fact의
 * 편집은 하나의 ChangeKey로 합쳐지므로 여기서 dedupe나 identity 확장을 할 필요가 없다.
 *
 * 문제가 있는 변경이 나머지 저장을 막지 않는다. 변경 하나하나를 검증해:
 * - 유효한 변경 → items(payload)
 * - 문제 있는 변경(식별 불가·유효성 실패) → rejected로 분리
 * - baseline과 동일(편집 후 원복) → payload 제외, 저장 후 정리 대상
 *
 * 호출부 계약: items만 저장하고, 성공 시 savedCellRefs만 store에서 제거한다.
 * rejected에 해당하는 변경은 store에 남아 사용자가 확인·수정할 수 있다.
 */

import type { FsType, SaveIxdChangeItem } from "@/apis/ixd-editor/types";
import { validateStoredCellValue } from "@/utils/ixd-editor/value-validation";
import type { CellIndexEntry, ChangeKey } from "@/utils/ixd-cell-model";
import type { SavedCellChange } from "./edit-session-store";

export interface RejectedCellChange {
  fsType: FsType;
  /** store에 남는 ChangeKey */
  changeKey: ChangeKey;
  value: string;
  reason: string;
  /**
   * user-input: 사용자 입력 문제(오타 등) — toast 안내로 충분, 모니터링 노이즈.
   * system: index 미매칭 등 불변식 위반 — Sentry로 보고해야 하는 버그 신호.
   */
  kind: "user-input" | "system";
}

export interface ValidatedChanges {
  items: SaveIxdChangeItem[];
  /** 저장에서 제외된 변경 — store에 남겨두고 사용자에게 알린다 */
  rejected: RejectedCellChange[];
  /**
   * 저장 성공 시 store에서 제거할 ChangeKey 목록 (rejected 제외).
   * value는 "저장 시점의 store 값" — 재조회가 도는 동안 사용자가 같은 셀을 다시
   * 고쳤는지 판별하는 데 쓴다(clearSavedChanges의 값 일치 가드).
   */
  savedCellRefs: SavedCellChange[];
}

interface ValidateAllChangesInput {
  consolidatedChanges: Record<ChangeKey, string>;
  separateChanges: Record<ChangeKey, string>;
  consolidatedIndex: Map<ChangeKey, CellIndexEntry>;
  separateIndex: Map<ChangeKey, CellIndexEntry>;
}

/**
 * 토스트/에러 메시지용 요약: 첫 사유 + 나머지 건수.
 * 사용자가 직접 조치할 수 있는 입력 문제(user-input)를 시스템 사유보다 먼저 노출한다
 * — "식별하지 못한 셀" 같은 시스템 메시지가 앞서면 정작 고칠 수 있는 오타가 묻힌다.
 */
export const summarizeRejections = (rejected: RejectedCellChange[]): string => {
  if (rejected.length === 0) return "";
  const first = rejected.find((r) => r.kind === "user-input") ?? rejected[0];
  const extra = rejected.length - 1;
  return extra > 0 ? `${first.reason} 외 ${extra}건` : first.reason;
};

export const validateAllChanges = ({
  consolidatedChanges,
  separateChanges,
  consolidatedIndex,
  separateIndex,
}: ValidateAllChangesInput): ValidatedChanges => {
  const items: SaveIxdChangeItem[] = [];
  const rejected: RejectedCellChange[] = [];
  const savedCellRefs: SavedCellChange[] = [];

  const process = (
    fsType: FsType,
    changes: Record<ChangeKey, string>,
    index: Map<ChangeKey, CellIndexEntry>,
  ) => {
    // 키에 normalizeCellId를 다시 걸면 안 된다 — identityKey에는 namespace URL의
    // `:`가 있어 cellId 전용 정규화가 키를 훼손한다.
    for (const [changeKey, value] of Object.entries(changes)) {
      const entry = index.get(changeKey);
      if (!entry) {
        // index에 없는 key는 시스템 상태 이상(index가 아직 빌드 안 됐거나 stale).
        // 이 변경만 제외하고 나머지는 저장한다 — store에 남겨 데이터 손실을 막는다.
        console.error("[DEBUG] IXD 저장 검증 — cell index 미매칭", {
          fsType,
          changeKey,
          value,
        });
        rejected.push({
          fsType,
          changeKey,
          value,
          reason: "식별하지 못한 셀입니다. 잠시 후 다시 저장해주세요.",
          kind: "system",
        });
        continue;
      }
      if (entry.baselineValue === value) {
        // 편집 후 원복 — 저장할 것은 없지만 저장 성공 시 store에서 정리한다.
        savedCellRefs.push({ fsType, changeKey, value });
        continue;
      }
      const validation = validateStoredCellValue({
        schemaType: entry.metadata.schemaType,
        storedValue: value,
      });
      if (!validation.ok) {
        console.error("[DEBUG] IXD 저장 검증 — 유효하지 않은 값", {
          fsType,
          changeKey,
          value,
          schemaType: entry.metadata.schemaType,
          errorMessage: validation.errorMessage,
        });
        rejected.push({
          fsType,
          changeKey,
          value,
          reason: validation.errorMessage,
          kind: "user-input",
        });
        continue;
      }
      items.push({
        value: validation.normalizedValue,
        metadata: entry.metadata,
      });
      // 가드 비교 대상은 store에 들어 있는 값(value)이다 — normalizedValue가 아니다.
      savedCellRefs.push({ fsType, changeKey, value });
    }
  };

  process("consolidated", consolidatedChanges, consolidatedIndex);
  process("separate", separateChanges, separateIndex);

  return { items, rejected, savedCellRefs };
};
